import { getApiHeaders } from 'Compass/profile-lab/services/headers';

declare const WEBSERVER_BASE_URL: string;

const handleApiError = (e: unknown, endpoint: string) => {
  console.error(`[ProfileLabService] Error calling ${endpoint}:`, e);
};

export interface ProfileStage {
  stage_id: string;
  has_questions: boolean;
  has_photo_selection: boolean;
  datasets: string[];
  previous_stages: string[];
  template_only: boolean;
  model_type: string | null;
}

interface ProfileStageDefinition {
  stage_id: string;
  template: string;
  questions: string | null;
  datasets: string[];
  previous_stages: string[];
  photo_selection_config: Record<string, unknown> | null;
  chunk_size: number;
  combiner_template: string | null;
  template_only: boolean;
  model_type: string | null;
}

export const fetchProfileStages = async (): Promise<ProfileStage[]> => {
  try {
    const response = await fetch(`${WEBSERVER_BASE_URL}/api/dazzle/profile-lab/stages/list`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch profile stages');
    }

    const data = await response.json();
    return data.stages;
  } catch (error) {
    handleApiError(error, 'fetchProfileStages');
    return [];
  }
};

export const fetchStageDefinition = async (stageId: string): Promise<ProfileStageDefinition | null> => {
  try {
    const response = await fetch(`${WEBSERVER_BASE_URL}/api/dazzle/profile-lab/stages/definition`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({ stage_id: stageId }),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch stage definition');
    }

    const data = await response.json();
    return data.stage;
  } catch (error) {
    handleApiError(error, 'fetchStageDefinition');
    return null;
  }
};

export const fetchSignedUrls = async (
  userLid: number,
  photoLids: number[]
): Promise<Record<number, string>> => {
  try {
    const response = await fetch(`${WEBSERVER_BASE_URL}/api/dazzle/profile-lab/photos/signed-urls`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({
        user_lid: userLid,
        photo_lids: photoLids,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch signed URLs');
    }

    const data = await response.json();
    return data.urls;
  } catch (error) {
    handleApiError(error, 'fetchSignedUrls');
    return {};
  }
};

export const listAssets = async (userLid: number) => {
  try {
    const response = await fetch(`${WEBSERVER_BASE_URL}/api/dazzle/profile-lab/photos/list`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({
        user_lid: userLid,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch photos: ${response.statusText}`);
    }

    const data = await response.json();
    return data.photos;
  } catch (error) {
    handleApiError(error, 'listAssets');
    throw error;
  }
};

export const computeSelection = async (
  userLid: number,
  operation: string,
  photoLids: number[],
  selectedLids: number[],
  params: Record<string, string | number | boolean>
) => {
  try {
    const response = await fetch(`${WEBSERVER_BASE_URL}/api/dazzle/profile-lab/selection/compute`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({
        user_lid: userLid,
        operation,
        photo_lids: photoLids,
        selected_lids: selectedLids,
        random_params: operation === 'random' ? params : undefined,
        latest_params: operation === 'latest' ? params : undefined,
        ranked_params: operation === 'ranked' ? params : undefined,
        stage_params: operation === 'stage' ? params : undefined,
      }),
    });

    if (!response.ok) {
      throw new Error(`Compute failed: ${response.statusText}`);
    }

    const result = await response.json();
    return result.photo_lids || [];
  } catch (error) {
    handleApiError(error, 'computeSelection');
    throw error;
  }
};

export const fetchDataset = async (
  userLid: number,
  datasetType: string
): Promise<string> => {
  try {
    const response = await fetch(`${WEBSERVER_BASE_URL}/api/dazzle/profile-lab/dataset/export`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({
        user_lid: userLid,
        dataset_type: datasetType,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch dataset: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    handleApiError(error, 'fetchDataset');
    throw error;
  }
};
